// Category 15 (Financed Emissions) — real API service for the Dashboard.
// BE returns raw numbers + structured codes only (see docs/cat15/cat15-fe-endpoint-guide.md);
// this layer maps the raw /v3/cat15 response into the presentation types in
// models/dto/cat15/cat15.types.ts and localises every code via i18next.

import axios, { AxiosResponse } from "axios";
import { t } from "i18next";
import apiClient from "../api/axios";
import { handleApiError } from "../api/handleApiError";
import { financedEmissions } from "../i18n";
import {
  ApiAttentionItemRow,
  ApiBorrowerDetailData,
  ApiBorrowerListData,
  ApiBorrowerListRow,
  ApiBulkLoanResult,
  ApiCertPresignResult,
  ApiConcentrationRiskRow,
  ApiCoverageSegmentRow,
  ApiDashboardData,
  ApiDetailEmissionRow,
  ApiDetailFinancialRow,
  ApiDetailLoanRow,
  ApiDetailSnapshotRow,
  ApiEnrichedSnapshotRow,
  ApiLoanListData,
  ApiLoanListRow,
  ApiReminderResult,
  ApiReportDownload,
  ApiReportGenerated,
  ApiReportHistoryList,
  ApiReportHistoryRow,
  ApiReportPreview,
  ApiRiskFlagRow,
  ApiMyDisclosure,
  ApiSector,
  ApiTrendValue,
  Cat15Envelope,
  CreateBorrowerBody,
  CreateLoanBody,
  GenerateReportBody,
  SectorOption,
  UpdateBorrowerBody,
  CreateSnapshotBody,
  UpdateSnapshotBody,
  FinancialDataBody,
  CertPresignBody,
  CertConfirmBody,
} from "../models/dto/cat15/cat15.api";
import {
  AllLoanRow,
  AssetClass,
  AttentionItem,
  BorrowerDetail,
  BorrowerListItem,
  BorrowerLoan,
  Cat15Filter,
  ConcentrationRisk,
  CoverageSegment,
  DashboardData,
  DqsLevel,
  EmissionSource,
  EmissionYear,
  FinancialYear,
  GeneratedReportFile,
  IntensityCoverage,
  IntensityCoverageCell,
  IntensityMetric,
  IntensityYoy,
  IssueType,
  ListingStatus,
  LoanSnapshot,
  LoanStatus,
  MyDisclosureView,
  ReportData,
  ReportFormat,
  ReportHistoryItem,
  RiskFlag,
  TrendValue,
} from "../models/dto/cat15/cat15.types";
import { intensityMetricLabelKey } from "../components/cat15/labels";

const BASE = "/v3/cat15";
/** localise within the financedEmissions namespace (service has no useTranslation) */
const tr = (key: string, opts?: Record<string, unknown>) =>
  t(key, { ns: financedEmissions, ...opts }) as string;

/* ---------- per-metric presentation rules (BE sends only value+delta) ---------- */
const KPI_PRESENTATION: Record<
  keyof DashboardData["kpis"],
  { deltaUnit: TrendValue["deltaUnit"]; higherIsBetter: boolean }
> = {
  financedEmissions: { deltaUnit: "%", higherIsBetter: false },
  outstanding: { deltaUnit: "%", higherIsBetter: false },
  coverage: { deltaUnit: "pp", higherIsBetter: true },
  weightedDqs: { deltaUnit: "pp", higherIsBetter: false },
  intensity: { deltaUnit: "%", higherIsBetter: false },
};

// The change as a fraction of the prior year; null when there is no prior to divide by.
const changeRatioOf = (raw: ApiTrendValue): number | null => {
  const prior = raw.value - raw.delta;
  return prior !== 0 ? raw.delta / Math.abs(prior) : null;
};

const toTrend = (
  raw: ApiTrendValue,
  metric: keyof DashboardData["kpis"],
): TrendValue => {
  const presentation = KPI_PRESENTATION[metric];
  // BE sends `delta` as the raw prior-year difference (value − prior). For "pp"
  // metrics (coverage, dqs) that difference IS the point change, so use it
  // as-is. For "%" metrics (emissions, outstanding, intensity) convert it to a
  // percentage change; otherwise a raw 42M-baht diff renders as "42000000%".
  const ratio = changeRatioOf(raw);
  const delta =
    presentation.deltaUnit === "%" ? (ratio == null ? 0 : Math.round(ratio * 1000) / 10) : raw.delta;
  return { value: raw.value, delta, ...presentation };
};

const everyBasis = <T>(value: T): Record<IntensityMetric, T> => ({
  outstanding: value,
  revenue: value,
  equity: value,
  evic: value,
});

// An older BE sends only the per-outstanding figure, so every other basis reads as having no data.
const outstandingOnly = <T>(outstanding: T, other: T): Record<IntensityMetric, T> => ({
  ...everyBasis(other),
  outstanding,
});

const legacyCoverage = (loans: number): IntensityCoverage =>
  outstandingOnly<IntensityCoverageCell>(
    { loans, totalLoans: loans, outstandingPct: 100 },
    { loans: 0, totalLoans: loans, outstandingPct: 0 },
  );

// Unrounded, like the BE's changePct.
const legacyYoy = (raw: ApiTrendValue): IntensityYoy => {
  const ratio = changeRatioOf(raw);
  return ratio == null ? { changePct: null, reason: "no_prior_data" } : { changePct: ratio * 100, reason: null };
};

/* ---------- code → i18n key maps ---------- */
const COVERAGE_LABEL_KEY: Record<string, string> = {
  covered: "Coverage-Code-Covered",
  no_snapshot: "Coverage-Code-No-Snapshot",
  no_financial_data: "Coverage-Code-No-Financial-Data",
  no_emissions_data: "Coverage-Code-No-Emissions-Data",
  no_sector: "Coverage-Code-No-Sector",
};

const CONCENTRATION_LABEL_KEY: Record<string, string> = {
  top5: "Risk-Top-5",
  single_sector: "Risk-Top-3-Sectors",
  single_borrower: "Risk-Largest-Borrower",
};

const RISK_FLAG_LABEL_KEY: Record<string, string> = {
  attribution: "Risk-Flag-Key-Attribution",
  stale: "Risk-Flag-Key-Stale",
  "high-intensity": "Risk-Flag-Key-High-Intensity",
};

const ATTENTION_DETAIL_KEY: Record<IssueType, string> = {
  "no-emissions": "Attention-Detail-No-Emissions",
  attribution: "Attention-Detail-Attribution",
  "not-activated": "Attention-Detail-Not-Activated",
  stale: "Attention-Detail-Stale",
  "missing-figure": "Attention-Detail-Missing-Figure",
};

// A type this build has no card, icon or label for would crash the attention cards, so it is dropped.
const isKnownIssue = (type: string): type is IssueType =>
  Object.prototype.hasOwnProperty.call(ATTENTION_DETAIL_KEY, type);

/* ---------- row mappers ---------- */
const mapCoverage = (c: ApiCoverageSegmentRow): CoverageSegment => ({
  code: c.labelCode,
  // component renders this string directly (no t()), so localise here
  label: COVERAGE_LABEL_KEY[c.labelCode]
    ? tr(COVERAGE_LABEL_KEY[c.labelCode])
    : c.labelCode,
  percent: c.percent,
  count: c.count,
});

const mapConcentration = (r: ApiConcentrationRiskRow): ConcentrationRisk => ({
  // component calls t(label) / t(note); pass an i18n key for the label and an
  // already-composed string for the note (t() returns unknown strings as-is).
  label: CONCENTRATION_LABEL_KEY[r.key] ?? r.key,
  percent: r.percent,
  threshold: r.threshold,
  breached: r.breached,
  note: r.components?.length
    ? r.components.map((c) => `${c.name} (${c.percent}%)`).join(" + ")
    : undefined,
});

const mapRiskFlag = (f: ApiRiskFlagRow): RiskFlag => ({
  label: RISK_FLAG_LABEL_KEY[f.key] ?? f.key,
  severity: f.severity,
});

const mapAttentionItem = (a: ApiAttentionItemRow, type: IssueType): AttentionItem => ({
  id: a.id,
  type,
  borrowerId: a.borrowerId,
  borrower: a.borrower,
  loanRef: a.loanRef ?? "",
  outstanding: a.outstanding,
  detail: tr(ATTENTION_DETAIL_KEY[type], {
    year: a.year,
    ...(a.metrics ? { figures: a.metrics.map((m) => tr(intensityMetricLabelKey(m))).join(", ") } : {}),
  }),
});

// drop the tsic fallback once the be stops sending tsic
const sectionCodeOf = (row: { sectionCode?: string; tsic?: string }): string => row.sectionCode ?? row.tsic ?? "";

/** Map the raw BE dashboard payload into the FE presentation shape. */
const mapDashboard = (d: ApiDashboardData): DashboardData => ({
  reportingYear: d.reportingYear,
  previousYear: d.previousYear,
  loanCount: d.loanCount,
  totalOutstanding: d.totalOutstanding,
  kpis: {
    financedEmissions: toTrend(d.kpis.financedEmissions, "financedEmissions"),
    outstanding: toTrend(d.kpis.outstanding, "outstanding"),
    coverage: toTrend(d.kpis.coverage, "coverage"),
    weightedDqs: toTrend(d.kpis.weightedDqs, "weightedDqs"),
    intensity: toTrend(d.kpis.intensity, "intensity"),
  },
  attentionSummary: d.attentionSummary.flatMap((a) =>
    isKnownIssue(a.type)
      ? [{ type: a.type, count: a.count, outstanding: a.outstanding, borrowers: a.borrowers }]
      : [],
  ),
  attentionTotal: d.attentionTotal,
  attentionItems: d.attentionItems.flatMap((a) => (isKnownIssue(a.type) ? [mapAttentionItem(a, a.type)] : [])),
  dqsDistribution: d.dqsDistribution.map((q) => ({
    level: q.level,
    percent: q.percent,
    count: q.count,
  })),
  coverageBreakdown: d.coverageBreakdown.map(mapCoverage),
  emissionsByAssetClass: d.emissionsByAssetClass.map((a) => ({
    assetClass: a.assetClass as AssetClass,
    value: a.value,
    percent: a.percent,
  })),
  concentrationRisk: d.concentrationRisk.map(mapConcentration),
  riskFlags: d.riskFlags.map(mapRiskFlag),
  topBorrowers: d.topBorrowers.map((b) => ({
    borrower: b.borrower,
    absolute: b.absolute,
    intensity: b.intensity,
    estimatedDqs: b.estimatedDqs,
    intensityByMetric: b.intensityByMetric ?? outstandingOnly<number | null>(b.intensity, null),
    // A borrower row's loan count is not on the wire.
    intensityCoverage: b.intensityCoverage ?? legacyCoverage(0),
  })),
  emissionsBySector: d.emissionsBySector.map((s) => ({
    sector: s.sector,
    sectorTh: s.sectorTh,
    sectionCode: sectionCodeOf(s),
    loans: s.loans,
    outstanding: s.outstanding,
    absolute: s.absolute,
    intensity: s.intensity,
    intensityByMetric: s.intensityByMetric ?? outstandingOnly<number | null>(s.intensity, null),
    intensityCoverage: s.intensityCoverage ?? legacyCoverage(s.loans),
  })),
  intensityByMetric: d.intensityByMetric ?? outstandingOnly<number | null>(d.kpis.intensity.value, null),
  intensityCoverage: d.intensityCoverage ?? legacyCoverage(d.loanCount),
  intensityScaleByMetric: d.intensityScaleByMetric ?? everyBasis(d.intensityScale),
  intensityYoyByMetric:
    d.intensityYoyByMetric ??
    outstandingOnly<IntensityYoy>(legacyYoy(d.kpis.intensity), { changePct: null, reason: "no_current_data" }),
  // yoyTrend / trendSummary intentionally omitted — the YoY-vs-NZBA chart was
  // removed from the dashboard and the BE endpoint no longer returns it.
});

/** Build the ?query= string for GET /dashboard from the filter bar state. */
const buildDashboardParams = (filter?: Partial<Cat15Filter>) => {
  const params = new URLSearchParams();
  params.set("year", String(filter?.reportingYear ?? new Date().getFullYear()));
  // ISIC is the borrower's primary sector taxonomy → dashboard groups/filters by ISIC.
  params.set("taxonomy", "isic");
  if (filter?.assetClass && filter.assetClass !== "all") {
    params.set("assetClass", filter.assetClass);
  }
  // sector must always reach BE as an array. BE ajv uses coerceTypes:true
  // (not "array"), so a single ?sector=X stays a string and fails "must be
  // array". Bracket notation (?sector[]=X) makes Express qs parse it as an
  // array even for one value: ?sector[]=C&sector[]=D.
  (filter?.sector ?? []).forEach((s) => params.append("sector[]", s));
  if (filter?.listingStatus && filter.listingStatus !== "all") {
    params.set("listingStatus", filter.listingStatus);
  }
  if (filter?.dqsLevel && filter.dqsLevel !== "all") {
    params.set("dqsLevel", String(filter.dqsLevel));
  }
  return params;
};

/** GET /v3/cat15/dashboard — portfolio dashboard for the acting lender org. */
export const getDashboardData = async (
  filter?: Partial<Cat15Filter>,
): Promise<DashboardData> => {
  try {
    const response: AxiosResponse<Cat15Envelope<ApiDashboardData>> =
      await apiClient.get(`${BASE}/dashboard`, {
        params: buildDashboardParams(filter),
      });
    return mapDashboard(response.data.results);
  } catch (error) {
    // handleApiError shows the alert and always throws (returns `never`).
    return handleApiError(error, {}, "error-get-dashboard");
  }
};

/** POST /v3/cat15/reminders — remind a batch of borrowers (lender action). */
export const sendReminders = async (
  borrowerIds: number[],
): Promise<ApiReminderResult> => {
  try {
    const response: AxiosResponse<Cat15Envelope<ApiReminderResult>> =
      await apiClient.post(`${BASE}/reminders`, { borrowerIds });
    return response.data.results;
  } catch (error) {
    return handleApiError(error, {}, "error-send-reminders");
  }
};

/** POST /v3/cat15/borrowers/:id/remind — remind a single borrower. */
export const sendReminder = async (
  borrowerId: number,
): Promise<ApiReminderResult> => {
  try {
    const response: AxiosResponse<Cat15Envelope<ApiReminderResult>> =
      await apiClient.post(`${BASE}/borrowers/${borrowerId}/remind`);
    return response.data.results;
  } catch (error) {
    return handleApiError(error, {}, "error-send-reminders");
  }
};

/**
 * GET /v3/cat15/sectors/:taxonomy — level-1 sections for the sector filter, or the
 * children of `parentRef` (the BOT industry codes under one section).
 * The dashboard filters by the level-1 section `code` (e.g. "C"), which is the
 * same value as `emissionsBySector[].sectionCode`.
 */
export const getSectors = async (
  taxonomy: "tsic" | "isic" = "tsic",
  parentRef?: string,
): Promise<SectorOption[]> => {
  try {
    return await fetchSectors(taxonomy, parentRef);
  } catch {
    // Non-blocking: a failed sector lookup shouldn't pop an alert — the filter
    // simply renders no sector options ("all sectors") until it loads.
    return [];
  }
};

/** Same lookup as getSectors, but a failed request rejects: the ISIC picker must not read an outage as "no codes". */
export const fetchSectors = async (
  taxonomy: "tsic" | "isic",
  parentRef?: string,
): Promise<SectorOption[]> => {
  const response: AxiosResponse<Cat15Envelope<ApiSector[]>> =
    await apiClient.get(`${BASE}/sectors/${taxonomy}`, {
      params: parentRef ? { parentRef } : { level: 1 },
    });
  return response.data.results.map((s) => ({
    id: s._id,
    value: s.code,
    labelEn: s.nameEn,
    labelTh: s.nameTh,
  }));
};

/* ===================== Borrowers / Loans ===================== */

/** BE sends dqs as a number, or null when the loan is excluded that year. */
const dqsFromApi = (dqs: number | null): DqsLevel | "excluded" =>
  dqs == null ? "excluded" : (dqs as DqsLevel);

// An empty listing is a real state the borrower is asked to fill, so it stays null rather than reading as "unlisted".
const toListingStatus = (raw: string | null): ListingStatus | null =>
  raw === "listed" || raw === "unlisted" ? raw : null;

const mapBorrowerListItem = (b: ApiBorrowerListRow): BorrowerListItem => ({
  // BE `id` is the numeric org id; the FE handle is a string everywhere.
  id: String(b.id),
  name: b.name,
  nameEn: b.nameEn,
  nameTh: b.nameTh,
  taxId: b.taxId,
  sector: b.sector,
  sectorTh: b.sectorTh,
  sectionCode: sectionCodeOf(b),
  sectorIsicCode: b.sectorIsicCode ?? null,
  sectorIsicNameEn: b.sectorIsicNameEn ?? null,
  sectorIsicNameTh: b.sectorIsicNameTh ?? null,
  listingStatus: toListingStatus(b.listingStatus),
  activated: Boolean(b.activated),
  loanCount: b.loanCount,
  financedEmissions: b.financedEmissions ?? null,
  classificationSource: b.classificationSource,
});

// Light snapshot from /detail (no per-year tco2e/dqs/updatedBy — those come from
// GET /loans/:refId/snapshots, lazy-loaded when a loan row is expanded).
const mapDetailSnapshot = (
  s: ApiDetailSnapshotRow,
  loanDqs: DqsLevel | "excluded",
): LoanSnapshot => ({
  year: s.year,
  outstanding: s.outstanding ?? 0,
  asOfDate: s.asOfDate ?? "",
  tco2e: 0,
  dqs: loanDqs,
  updatedBy: "",
  updatedAt: "",
});

const mapDetailLoan = (l: ApiDetailLoanRow): BorrowerLoan => {
  const dqs = dqsFromApi(l.dqs);
  return {
    id: l.id,
    loanRef: l.loanRef ?? "",
    assetClass: l.assetClass as AssetClass,
    principal: l.principal,
    outstanding: l.outstanding ?? 0,
    origination: l.origination,
    status: l.status as LoanStatus,
    tco2e: l.tco2e ?? 0,
    dqs,
    snapshots: l.snapshots.map((s) => mapDetailSnapshot(s, dqs)),
  };
};

// evic/equity/debt/revenue are shown in raw baht (BE's canonical unit) — the
// Financial Data table labels them "(THB)" and displays the value as-is (no
// M-THB scaling). The /report endpoint still sends M THB via its own mapper
// (mapReportPreview), which is unaffected by this.
const mapFinancial = (f: ApiDetailFinancialRow): FinancialYear => ({
  year: f.year,
  evic: f.evic ?? 0,
  equity: f.equity ?? 0,
  debt: f.debt ?? 0,
  revenue: f.revenue ?? 0,
  asOfDate: f.asOfDate ?? "",
});

const mapEmission = (e: ApiDetailEmissionRow): EmissionYear => ({
  year: e.year,
  scope1: e.scope1,
  scope2: e.scope2,
  s1s2: e.s1s2,
  dqs: e.dqs as DqsLevel,
  source: e.source as EmissionSource,
  lastUpdated: e.updatedAt ?? "",
  certificateAttached: e.certificateAttached ?? false,
  certificateUrl: e.certificateUrl ?? null,
  certificateFileName: e.certificateFileName ?? null,
  certificateSize: e.certificateSize ?? null,
  certificateUploadedAt: e.certificateUploadedAt ?? null,
  certificateVerifier: e.certificateVerifier ?? null,
  certificateVerificationDate: e.certificateVerificationDate ?? null,
});

const mapBorrowerDetail = (d: ApiBorrowerDetailData): BorrowerDetail => ({
  id: String(d.id),
  name: d.name,
  nameEn: d.nameEn,
  nameTh: d.nameTh,
  taxId: d.taxId,
  sector: d.sector,
  sectorTh: d.sectorTh,
  sectionCode: sectionCodeOf(d),
  sectorIsicCode: d.sectorIsicCode ?? null,
  sectorIsicNameEn: d.sectorIsicNameEn ?? null,
  sectorIsicNameTh: d.sectorIsicNameTh ?? null,
  listingStatus: toListingStatus(d.listingStatus),
  loanCount: d.loanCount,
  classificationSource: d.classificationSource,
  summary: {
    financedEmissions: d.summary.financedEmissions,
    activeLoans: d.summary.activeLoans,
    coverage: d.summary.coverage,
    weightedDqs: d.summary.weightedDqs,
    yoyTrend: d.summary.yoyTrend,
    byAssetClass: d.summary.byAssetClass.map((a) => ({
      assetClass: a.assetClass as AssetClass,
      value: a.value,
      percent: a.percent,
    })),
  },
  loans: d.loans.map(mapDetailLoan),
  financials: d.financials.map(mapFinancial),
  emissions: d.emissions.map(mapEmission),
});

const mapEnrichedSnapshot = (s: ApiEnrichedSnapshotRow): LoanSnapshot => ({
  year: s.year,
  outstanding: s.outstanding ?? 0,
  asOfDate: s.asOfDate ?? "",
  tco2e: s.tco2e ?? 0,
  dqs: dqsFromApi(s.dqs),
  updatedBy: s.updatedBy?.name ?? "",
  updatedAt: s.updatedAt ?? "",
});

const mapLoanListRow = (l: ApiLoanListRow): AllLoanRow => ({
  id: l.id,
  loanRef: l.loanRef ?? "",
  borrower: l.borrower ?? "",
  assetClass: l.assetClass as AssetClass,
  principal: l.principal,
  outstanding: l.outstanding ?? 0,
  origination: l.origination,
  status: l.status as LoanStatus,
  tco2e: l.tco2e ?? 0,
  dqs: dqsFromApi(l.dqs),
  // Snapshots are lazy-loaded on expand (the list endpoint omits them).
  snapshots: [],
});

const BORROWER_PAGE_LIMIT = 100;

/**
 * Total borrowers in the bank's book. The list call above only returns the first
 * page, so its length is not the total — read it from the pagination envelope.
 */
export const getBorrowerCount = async (): Promise<number> => {
  try {
    const response: AxiosResponse<Cat15Envelope<ApiBorrowerListData>> =
      await apiClient.get(`${BASE}/borrowers`, { params: { page: 1, limit: 1 } });
    return response.data.results.pagination.totalItems;
  } catch (error) {
    return handleApiError(error, {}, "error-get-borrowers");
  }
};

/** GET /v3/cat15/borrowers — first page only (client-side filtered list view). */
export const getBorrowers = async (): Promise<BorrowerListItem[]> => {
  try {
    const response: AxiosResponse<Cat15Envelope<ApiBorrowerListData>> =
      await apiClient.get(`${BASE}/borrowers`, {
        params: { page: 1, limit: BORROWER_PAGE_LIMIT },
      });
    return response.data.results.items.map(mapBorrowerListItem);
  } catch (error) {
    return handleApiError(error, {}, "error-get-borrowers");
  }
};

/**
 * GET /v3/cat15/borrowers across ALL pages. Used by bulk import to resolve
 * taxId → borrowerId — a single 100-row page would wrongly "skip" loans whose
 * borrower sits on page 2+.
 */
export const getAllBorrowers = async (): Promise<BorrowerListItem[]> => {
  try {
    const first: AxiosResponse<Cat15Envelope<ApiBorrowerListData>> =
      await apiClient.get(`${BASE}/borrowers`, {
        params: { page: 1, limit: BORROWER_PAGE_LIMIT },
      });
    const { items, pagination } = first.data.results;
    const all = items.map(mapBorrowerListItem);
    if (pagination.totalPages <= 1) return all;

    // Fetch the remaining pages in parallel (not one-by-one) so wall-clock is
    // bounded by the slowest single request, not the sum of all pages.
    const restPages = Array.from(
      { length: pagination.totalPages - 1 },
      (_, i) => i + 2,
    );
    const responses = await Promise.all(
      restPages.map((page) =>
        apiClient.get<Cat15Envelope<ApiBorrowerListData>>(`${BASE}/borrowers`, {
          params: { page, limit: BORROWER_PAGE_LIMIT },
        }),
      ),
    );
    responses.forEach((res) =>
      all.push(...res.data.results.items.map(mapBorrowerListItem)),
    );
    return all;
  } catch (error) {
    return handleApiError(error, {}, "error-get-borrowers");
  }
};

/** GET /v3/cat15/borrowers/:id/detail — composite detail for one borrower-year. */
export const getBorrowerDetail = async (
  id: string,
  year: number = new Date().getFullYear(),
): Promise<BorrowerDetail> => {
  try {
    const response: AxiosResponse<Cat15Envelope<ApiBorrowerDetailData>> =
      await apiClient.get(`${BASE}/borrowers/${id}/detail`, {
        params: { year },
      });
    return mapBorrowerDetail(response.data.results);
  } catch (error) {
    return handleApiError(error, {}, "error-get-borrower-detail");
  }
};

/** GET /v3/cat15/loans — all loans across the portfolio (client-side filtered). */
export const getAllLoans = async (): Promise<AllLoanRow[]> => {
  try {
    const response: AxiosResponse<Cat15Envelope<ApiLoanListData>> =
      await apiClient.get(`${BASE}/loans`, {
        params: { page: 1, limit: 100 },
      });
    return response.data.results.loans.map(mapLoanListRow);
  } catch (error) {
    return handleApiError(error, {}, "error-get-loans");
  }
};

/** GET /v3/cat15/loans/:refId/snapshots — enriched per-year rows for the
 *  expand panel (carries tco2e/dqs the list/detail rows don't). */
export const getLoanSnapshots = async (
  refId: string,
): Promise<LoanSnapshot[]> => {
  try {
    const response: AxiosResponse<Cat15Envelope<ApiEnrichedSnapshotRow[]>> =
      await apiClient.get(`${BASE}/loans/${refId}/snapshots`);
    return response.data.results
      .map(mapEnrichedSnapshot)
      .sort((a, b) => b.year - a.year);
  } catch (error) {
    return handleApiError(error, {}, "error-get-loan-snapshots");
  }
};

/** POST /v3/cat15/borrowers — create (or idempotently return) a borrower. */
export const createBorrower = async (
  body: CreateBorrowerBody,
): Promise<BorrowerDetail> => {
  try {
    const response: AxiosResponse<Cat15Envelope<ApiBorrowerDetailData>> =
      await apiClient.post(`${BASE}/borrowers`, body);
    return mapBorrowerDetail(response.data.results);
  } catch (error) {
    return handleApiError(
      error,
      { CAT15_BORROWER_TAXID_TAKEN: "error-borrower-taxid-taken" },
      "error-create-borrower",
    );
  }
};

/** PATCH /v3/cat15/borrowers/:id — edit a borrower (taxId not editable). */
export const updateBorrower = async (
  id: string,
  body: UpdateBorrowerBody,
): Promise<BorrowerDetail> => {
  try {
    const response: AxiosResponse<Cat15Envelope<ApiBorrowerDetailData>> =
      await apiClient.patch(`${BASE}/borrowers/${id}`, body);
    return mapBorrowerDetail(response.data.results);
  } catch (error) {
    return handleApiError(error, {}, "error-update-borrower");
  }
};

/** POST /v3/cat15/loans — attach a loan to a borrower. */
export const createLoan = async (
  body: CreateLoanBody,
): Promise<void> => {
  try {
    await apiClient.post(`${BASE}/loans`, body);
  } catch (error) {
    // handleApiError always throws (returns never); throw explicitly so the
    // error still propagates to the caller even if that ever changes.
    throw handleApiError(error, {}, "error-create-loan");
  }
};

/**
 * PATCH /v3/cat15/loans/:refId/snapshots/:year — edit a snapshot's outstanding.
 * Only `outstandingAmount` (raw THB) + `asOfDate` are editable; per-year tco2e
 * and dqs are engine-computed and cannot be set from the FE.
 */
export const updateLoanSnapshot = async (
  refId: string,
  year: number,
  body: UpdateSnapshotBody,
): Promise<void> => {
  try {
    await apiClient.patch(`${BASE}/loans/${refId}/snapshots/${year}`, body);
  } catch (error) {
    // handleApiError always throws (returns never); throw explicitly so the
    // error still propagates to the caller even if that ever changes.
    throw handleApiError(error, {}, "error-update-snapshot");
  }
};

/**
 * POST /v3/cat15/loans/:refId/snapshots — add a new year-end snapshot.
 * One snapshot per loan-year (BE returns 409 on a duplicate). tco2e/dqs are
 * engine-computed from the outstanding + borrower financials.
 */
export const createLoanSnapshot = async (
  refId: string,
  body: CreateSnapshotBody,
): Promise<void> => {
  try {
    await apiClient.post(`${BASE}/loans/${refId}/snapshots`, body);
  } catch (error) {
    throw handleApiError(error, {}, "error-create-snapshot");
  }
};

/**
 * POST /v3/cat15/borrowers/:id/financial-data — add a year's company-value
 * figures (bank-entered). One row per borrower-year (duplicate → 409); BE
 * requires ≥1 figure. `body` carries raw baht (the UI collects/shows THB
 * directly — no M-THB scaling).
 */
export const setFinancialData = async (
  borrowerId: string,
  body: FinancialDataBody,
): Promise<void> => {
  try {
    await apiClient.post(`${BASE}/borrowers/${borrowerId}/financial-data`, body);
  } catch (error) {
    throw handleApiError(error, {}, "error-set-financial");
  }
};

/**
 * PATCH /v3/cat15/borrowers/:id/financial-data/:year — edit a year's figures.
 * Year comes from the path; the body carries only the (raw-baht) figures.
 */
export const updateFinancialData = async (
  borrowerId: string,
  year: number,
  body: Omit<FinancialDataBody, "year">,
): Promise<void> => {
  try {
    await apiClient.patch(
      `${BASE}/borrowers/${borrowerId}/financial-data/${year}`,
      body,
    );
  } catch (error) {
    throw handleApiError(error, {}, "error-update-financial");
  }
};

/**
 * Attach an audit certificate for a borrower-year (bank side). Three steps:
 * presign → PUT the file straight to S3 (raw axios, no auth header, mirrors
 * evidence upload) → confirm. The BE reads the object back from S3 on confirm,
 * so the file bytes never pass through our API.
 */
export const attachBorrowerCertificate = async (
  borrowerId: string,
  year: number,
  file: File,
  meta?: { verifierOrgName?: string; verificationDate?: string; standard?: string },
  onProgress?: (percent: number) => void,
): Promise<void> => {
  try {
    const presignBody: CertPresignBody = { year, fileName: file.name };
    const presign: AxiosResponse<Cat15Envelope<ApiCertPresignResult>> =
      await apiClient.post(
        `${BASE}/borrowers/${borrowerId}/audit-certs/presign`,
        presignBody,
      );
    const { uploadUrl, s3FileName } = presign.data.results;

    await axios.put(uploadUrl, file, {
      headers: { "Content-Type": file.type },
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total));
      },
    });

    const confirmBody: CertConfirmBody = {
      year,
      s3FileName,
      originalFileName: file.name,
      verifierOrgName: meta?.verifierOrgName,
      verificationDate: meta?.verificationDate,
      standard: meta?.standard,
    };
    await apiClient.post(
      `${BASE}/borrowers/${borrowerId}/audit-certs`,
      confirmBody,
    );
  } catch (error) {
    throw handleApiError(error, {}, "error-attach-certificate");
  }
};

/** DELETE /v3/cat15/borrowers/:id/audit-certs/:year — soft-delete a borrower-year's audit certificate (bank side). */
export const deleteBorrowerCertificate = async (
  borrowerId: string,
  year: number,
): Promise<void> => {
  try {
    await apiClient.delete(`${BASE}/borrowers/${borrowerId}/audit-certs/${year}`);
  } catch (error) {
    throw handleApiError(error, {}, "error-delete-certificate");
  }
};

/* ===================== PCAF Report ===================== */

// Reports keep asset classes in their internal LOAN_ASSET_CLASS form; the FE
// presentation model uses the short wire form (matches the dashboard).
const REPORT_ASSET_CLASS_TO_FE: Record<string, AssetClass> = {
  business_loans_unlisted: "business",
  listed_equity_bonds: "listed",
  project_finance: "project",
};
// An unmapped class passes through raw so its row shows the code — never relabelled as another class.
const toFeAssetClass = (internal: string): AssetClass => {
  const fe = REPORT_ASSET_CLASS_TO_FE[internal];
  if (fe) return fe;
  console.error(`Unmapped report asset class: ${internal}`);
  return internal as AssetClass;
};

// BE encodes DQS rows as "1".."5" | "N/A"; the excluded bucket maps to "excluded".
export const toDqsLevel = (dqs: string): DqsLevel | "excluded" => {
  const level = Number(dqs);
  return Number.isInteger(level) && level >= 1 && level <= 5 ? (level as DqsLevel) : "excluded";
};

/** Human-readable label for a set of formats, e.g. ["excel","pdf"] → "Excel + PDF". */
const formatLabel = (formats: ReportFormat[]): string =>
  formats.map((f) => (f === "excel" ? "Excel" : "PDF")).join(" + ");

// Report concentration metric label per code (localised via the financedEmissions namespace).
const REPORT_CONCENTRATION_LABEL_KEY: Record<string, string> = {
  top5_borrowers: "Conc-Top5-Borrowers",
  single_borrower: "Conc-Single-Borrower",
  single_sector: "Conc-Single-Sector",
  top3_sectors: "Conc-Top3-Sectors",
  attribution_over_one: "Conc-Attribution-Over-One",
};

// Maps the raw 9-section preview payload into the presentation ReportData the
// preview UI (and the mock) already consume.
const mapReportPreview = (api: ApiReportPreview): ReportData => {
  const { cover, absolute, intensity, coverage, dqs, sector, dataGap, footnotes } = api;

  // coverage.percent is outstanding-weighted (matches the dashboard); by-count is derived from loan counts.
  const coverageByOutstanding = coverage.percent;
  const coverageByCount = coverage.totalLoans > 0 ? Math.round((coverage.includedLoans / coverage.totalLoans) * 1000) / 10 : 0;

  // Intensity rows carry only the ratio; borrow outstanding/financed from the absolute section.
  const absByClass = new Map(absolute.rows.map((r) => [r.assetClass, r]));

  // Group the per-loan data gaps into one row per exclusion reason.
  const exclusionsByReason = new Map<string, { reason: string; loans: number; outstandingM: number }>();
  for (const row of dataGap.rows) {
    const entry = exclusionsByReason.get(row.reason) ?? { reason: row.reason, loans: 0, outstandingM: 0 };
    entry.loans += 1;
    entry.outstandingM += row.outstanding ?? 0;
    exclusionsByReason.set(row.reason, entry);
  }

  return {
    institution: cover.institution,
    year: cover.year,
    period: `${cover.periodStart} – ${cover.periodEnd}`,
    methodology: cover.methodology,
    totalEmissions: cover.totalFinanced,
    outstanding: absolute.total.outstanding,
    coverage: coverage.percent,
    weightedDqs: dqs.weightedAverage,
    generatedAt: cover.generatedAt,
    absolute: absolute.rows.map((r) => ({
      assetClass: toFeAssetClass(r.assetClass),
      loans: r.loans,
      outstandingM: r.outstanding,
      financed: r.financed,
      percent: r.sharePct,
    })),
    intensity: intensity.rows.map((r) => {
      const abs = absByClass.get(r.assetClass);
      return {
        assetClass: toFeAssetClass(r.assetClass),
        outstandingM: abs?.outstanding ?? 0,
        financed: abs?.financed ?? 0,
        intensity: r.intensity,
        intensityByMetric: r.intensityByMetric ?? outstandingOnly<number | null>(r.intensity, null),
      };
    }),
    intensityTotal:
      intensity.weightedAverage.intensityByMetric ??
      outstandingOnly<number | null>(intensity.weightedAverage.intensity, null),
    intensityCoverage: intensity.weightedAverage.intensityCoverage,
    missingFigures: (dataGap.missingFigures ?? []).map((r) => ({
      borrower: r.borrowerName,
      metrics: r.metrics,
      outstandingM: r.outstanding,
    })),
    missingFiguresCount: dataGap.missingFiguresCount ?? 0,
    coverageByCount,
    coverageByOutstanding,
    exclusions: Array.from(exclusionsByReason.values()),
    dqsDistribution: dqs.rows.map((r) => ({
      level: toDqsLevel(r.dqs),
      percent: r.outstandingSharePct,
      count: r.loans,
    })),
    sectors: sector.rows.map((r) => ({
      sector: r.sectorName,
      loans: r.loans,
      outstandingM: r.outstanding,
      financed: r.financed,
      percent: r.sharePct,
    })),
    dataGap: dataGap.rows.map((r) => ({
      borrower: r.borrowerName,
      loanRef: r.loanRef ?? "—",
      reason: r.reason,
      outstandingM: r.outstanding ?? 0,
    })),
    footnotes: [
      tr("Footnote-Fallback-Year", { count: footnotes.fallbackYearLoans }),
      tr("Footnote-Cert-Backed", { count: footnotes.certBackedLoans }),
      tr("Footnote-Attribution", { count: footnotes.attributionOverOneLoans }),
      footnotes.projectFinanceNote,
      tr("Footnote-Version", { version: footnotes.version }),
    ],
    executive: {
      kpis: api.executive.kpis,
      keyFindings: api.executive.keyFindings,
      actionItems: {
        rows: api.executive.actionItems.rows.map((r) => ({
          issue: r.issue,
          count: r.count,
          outstandingM: r.outstanding,
          action: r.action,
        })),
        total: { count: api.executive.actionItems.total.count, outstandingM: api.executive.actionItems.total.outstanding },
      },
    },
    concentration: api.concentration.rows.map((r) => ({
      metricCode: r.metricCode,
      metric: tr(REPORT_CONCENTRATION_LABEL_KEY[r.metricCode] ?? r.metricCode),
      current: r.currentText,
      threshold: r.thresholdPct != null ? `≤ ${r.thresholdPct}%` : "—",
      status: r.status,
    })),
    topBorrowers: {
      rows: api.topBorrowers.rows.map((r) => ({
        rank: r.rank,
        borrower: r.borrower,
        sector: r.sector,
        outstandingM: r.outstanding,
        financed: r.financed,
        intensity: r.intensity,
        percent: r.sharePct,
        estimatedDqs: r.estimatedDqs,
      })),
      top5SharePct: api.topBorrowers.top5SharePct,
      top10SharePct: api.topBorrowers.top10SharePct,
    },
  };
};

/** GET /v3/cat15/report — full report preview for a reporting year, optionally scoped by sector. */
export const getReportData = async (
  year: number,
  taxonomy: "tsic" | "isic" = "isic",
  sectors?: string[],
): Promise<ReportData> => {
  try {
    const params = new URLSearchParams();
    params.set("year", String(year));
    params.set("taxonomy", taxonomy);
    // Bracket notation so Express (qs) parses a single value as an array too (matches the dashboard filter).
    (sectors ?? []).forEach((s) => params.append("sector[]", s));
    const response: AxiosResponse<Cat15Envelope<ApiReportPreview>> =
      await apiClient.get(`${BASE}/report`, { params });
    return mapReportPreview(response.data.results);
  } catch (error) {
    return handleApiError(error, {}, "error-get-report");
  }
};

/**
 * POST /v3/cat15/loans/bulk — create up to 500 loans in one call. Each loan is
 * keyed by numeric `borrowerId` (resolve taxId → borrowerId before calling).
 * Returns per-row created/failed counts.
 */
export const createBulkLoans = async (
  loans: CreateLoanBody[],
): Promise<ApiBulkLoanResult> => {
  try {
    const response: AxiosResponse<Cat15Envelope<ApiBulkLoanResult>> =
      await apiClient.post(`${BASE}/loans/bulk`, { loans });
    return response.data.results;
  } catch (error) {
    return handleApiError(error, {}, "error-bulk-import-loans");
  }
};

/**
 * POST /v3/cat15/report/generate — build the requested formats server-side,
 * store them, and return each file with a short-lived presigned download URL.
 */
export const generateReport = async (
  year: number,
  formats: ReportFormat[],
  taxonomy: "tsic" | "isic" = "isic",
  sectors?: string[],
): Promise<GeneratedReportFile[]> => {
  try {
    const body: GenerateReportBody = {
      year,
      taxonomy,
      formats,
      ...(sectors && sectors.length ? { sector: sectors } : {}),
    };
    const response: AxiosResponse<Cat15Envelope<ApiReportGenerated>> =
      await apiClient.post(`${BASE}/report/generate`, body);
    return response.data.results.files.map((f) => ({
      format: f.format,
      fileName: f.originalFileName,
      size: f.size,
      url: f.url,
    }));
  } catch (error) {
    return handleApiError(error, {}, "error-generate-report");
  }
};

/** GET /v3/cat15/report/history — past generations for the acting lender. */
export const getReportHistory = async (
  year?: number,
  page?: number,
  limit?: number,
): Promise<ReportHistoryItem[]> => {
  try {
    const response: AxiosResponse<Cat15Envelope<ApiReportHistoryList>> =
      await apiClient.get(`${BASE}/report/history`, {
        params: { year, page, limit },
      });
    return response.data.results.items.map((row: ApiReportHistoryRow) => {
      const formats = row.files.map((f) => f.format);
      return {
        id: row._id,
        generatedOn: row.createdAt ?? "",
        year: row.year,
        format: formatLabel(formats),
        formats,
        by: row.generatedBy || "—",
        status: "ready", // a stored generation always succeeded (synchronous build)
      };
    });
  } catch (error) {
    return handleApiError(error, {}, "error-get-report-history");
  }
};

/**
 * GET /v3/cat15/report/history/:refId/download — presigned URL for one stored
 * file. `format` selects which file when a generation produced several.
 */
export const getReportDownloadUrl = async (
  refId: string,
  format?: ReportFormat,
): Promise<{ url: string; fileName?: string }> => {
  try {
    const response: AxiosResponse<Cat15Envelope<ApiReportDownload>> =
      await apiClient.get(`${BASE}/report/history/${refId}/download`, {
        params: format ? { format } : undefined,
      });
    const { url, originalFileName } = response.data.results;
    return { url, fileName: originalFileName ?? undefined };
  } catch (error) {
    return handleApiError(error, {}, "error-download-report");
  }
};

/**
 * GET /v3/cat15/me/emissions-disclosure — the borrower's own PCAF data as held
 * by their lender (read-only My Data view). ISO dates are trimmed to YYYY-MM-DD.
 */
export const getMyEmissionsDisclosure = async (): Promise<MyDisclosureView> => {
  try {
    const response: AxiosResponse<Cat15Envelope<ApiMyDisclosure>> =
      await apiClient.get(`${BASE}/me/emissions-disclosure`);
    const { lender, rows } = response.data.results;
    return {
      lender: lender ?? "",
      rows: rows.map((r) => ({
        year: r.year,
        verifier: r.verifier ?? "",
        verifiedOn: (r.verifiedOn ?? "").slice(0, 10),
        emission: r.emission,
        asOfDate: (r.asOfDate ?? "").slice(0, 10),
        dqs: r.dqs as DqsLevel,
        certificateUrl: r.certificateUrl ?? null,
      })),
    };
  } catch (error) {
    return handleApiError(error, {}, "error-get-my-data");
  }
};
